// Imports
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import currency from 'currency.js';
import theme from '../../../../../../shareables/theme';
import { useAppDispatch, useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import APIForm, { APIFormOptions } from '../../../../../../shareables/integrations/api/components/form';
import { GetPaymentOptionResponse } from '../../../../../../shareables/types/api/main/website/payment-option/get';
import {
	GetPaymentIntentParameters,
	GetPaymentIntentResponse,
} from '../../../../../../shareables/types/api/main/website/payment-option/payment-intent/get';
import parseAndSanitizeMarkdown from '../../../../../../shareables/utils/markdown/parse-and-sanitize';
import formatCurrency from '../../../../../../shareables/utils/project-specific/formatting/format-currency';
import { StyledEditorGeneratedContentContainer } from '../../page-types/shared-components/styled-editor-generated-content-container';
import { StripePaymentElementRef } from './stripe-payment-element';
import StripeElements from './stripe-elements';
import Select from '../../../../../../shareables/foundation/front-end/components/forms/fields/select';
import Input from '../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import { GeneratedThemeProps } from '../../../../../templates/template-style';
import {
	GetFeeParameters,
	GetFeeResponse,
} from '../../../../../../shareables/types/api/main/website/payment-option/fee/get';
import { GetPaymentMethodsResponse } from '../../../../../../shareables/types/api/main/website/member/payment-methods/get';
import Skeleton from 'react-loading-skeleton';
import isScreenshot from '../../../../../../shareables/utils/is-screenshot';
import FormSubmitted from '../../page-types/shared-components/form-submitted';
import AnimateHeight from 'react-animate-height';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useBankAccountPayment from './hooks/use-bank-account-payment';
import useSavedCardPayment from './hooks/use-saved-card-payment';
import helperFunctions from '../../../../../../shareables/foundation/front-end/utils/helper-functions';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import FieldErrorMessage from '../../../../../../shareables/foundation/front-end/components/forms/field-error-message';
import { Helmet } from 'react-helmet-async';
import PageTitle from '../../shared-components/page-title';
import Heading from '../../shared-components/page-title/heading';
import Notice from '../../../../notice';
import Button from '../../../../../../shareables/foundation/front-end/components/buttons/standard';
import determinePaymentMethodTypeStatuses from '../utils/determine-payment-method-type-statuses';
import UnavailablePaymentMethodTypeNotice from '../shared-components/unavailable-payment-method-type-notice';


// Styled components
const Notes = styled(StyledEditorGeneratedContentContainer)`
	line-height: 1.3125rem;
	
	p {
		margin-bottom: 1.3em;
	}
`;

const Panel = styled.div`
	margin-bottom: 1.25rem;
	background-color: #fff;
	border: 0.0625rem solid #ddd;
	border-radius: 0.25rem;
	box-shadow: 0 0.0625rem 0.0625rem rgba(0, 0, 0, 0.05);
	
	padding: 0.9375rem;
	line-height: 1.3125rem;
`;

const Placeholder = styled(Panel)`
	margin-bottom: 0;
	box-shadow: none;
`;

const ConfirmAndPayPanel = styled(Panel)`
	margin-bottom: 0;
	border-bottom: none;
	border-bottom-left-radius: 0;
	border-bottom-right-radius: 0;
	padding-bottom: 0.875rem;
	
	~ div {
		background-color: #fff;
		border-left: 0.0625rem solid #ddd;
		border-right: 0.0625rem solid #ddd;
		padding: 0 0.9375rem;
	}
`;

const ConfirmAndPayFooter = styled.div`
	display: flow-root;
	
	background-color: #fff;
	border: 0.0625rem solid #ddd;
	border-top: none;
	border-bottom-left-radius: 0.25rem;
	border-bottom-right-radius: 0.25rem;
	box-shadow: 0 0.0625rem 0.0625rem rgba(0, 0, 0, 0.05);
	margin-bottom: 1.25rem;
	padding: 0 0.9375rem 0.9375rem;
`;

const Header = styled.h3`
	font-size: 1.5rem;
	margin-bottom: 0.625rem;
	line-height: 1.1;
	font-weight: 500;
`;

const Amount = styled.div`
	margin-top: 1.48rem;
`;

const PayNow = styled.div<{ moreSpace: boolean }>`
	form:not(:last-child) {
		margin-bottom: 0;
	}
	
	button {
		${({ moreSpace }) =>
			moreSpace &&
			css`
				margin-top: 0.625rem;
			`}
	}
`;

const Mandate = styled.div`
	margin-top: 1rem;
	line-height: 1.1rem;
`;

const PaymentMethodType = styled.p<{ theme: GeneratedThemeProps }>`
	color: ${(props) => props.theme.contentLinkColor};
	cursor: pointer;
	
	&:hover,
	&:focus {
		text-decoration: underline;
	}
`;

const ChevronIcon = styled(FontAwesomeIcon)<{ $isExpanded: boolean }>`
	margin-right: 0.3125rem;
	
	transition: transform 0.25s ease-in-out;
	transform: rotate(${(props) => (props.$isExpanded ? '90deg' : '0deg')});
`;

const ContentSection = styled.div<{ open: boolean }>`
	display: grid;
	grid-template-rows: ${({ open }) => (open ? '1fr' : '0fr')};
	transition:
		grid-template-rows 400ms ease,
		opacity 400ms ease;
	opacity: ${({ open }) => (open ? 1 : 0)};
`;

const ContentSectionInner = styled.div`
	overflow: hidden;
	min-height: 0;
	margin-left: -0.25rem;
	padding-left: 0.25rem;
	margin-right: -0.25rem;
	padding-right: 0.25rem;
	margin-bottom: -0.25rem;
	padding-bottom: 0.25rem;
`;

const MethodContent = styled.div<{ bottomSpacing?: boolean; marginTop?: boolean }>`
	${({ marginTop }) =>
		marginTop &&
		css`
			margin-top: 0.5rem;
		`}
	
	${({ bottomSpacing }) =>
		bottomSpacing &&
		css`
			padding-bottom: 0.25rem;
			margin-bottom: 0.75rem;
		`};
`;

const CardSelectorWrapper = styled.div<{ isNewCardSelected: boolean }>`
	margin-bottom: ${({ isNewCardSelected }) => (isNewCardSelected ? '1.25em' : '0')};
	transition: margin-bottom 400ms ease;
`;

const MinimumAmountWarning = styled.em`
	color: ${theme.colors.danger};
`;


// Define what a card looks like
export type Card = Extract<GetPaymentMethodsResponse['paymentMethods'][0], { type: 'CARD' }>;


// Define the accepted props
interface Props {
	storybookPaymentOption?: GetPaymentOptionResponse;
	storybookSelectedAmount?: GetPaymentOptionResponse['amounts'][0];
	storybookSelectedPaymentMethod?: GetPaymentOptionResponse['paymentMethods'][0];
	storybookFee?: GetFeeResponse;
	storybookPaymentMethods?: GetPaymentMethodsResponse;
}


// Function component
const MakePayment: React.FC<Props> = ({
	storybookPaymentOption,
	storybookSelectedAmount,
	storybookSelectedPaymentMethod,
	storybookFee,
	storybookPaymentMethods,
}) => {
	// Use state
	const [paymentOption, setPaymentOption] = useState<GetPaymentOptionResponse | null>(storybookPaymentOption ?? null);
	const [selectedAmount, setSelectedAmount] = useState<GetPaymentOptionResponse['amounts'][0] | null>(
		storybookSelectedAmount ?? null
	);
	
	const [chosenPaymentMethodType, setChosenPaymentMethodType] = useState<
		GetPaymentOptionResponse['paymentMethods'][0] | null
	>(storybookSelectedPaymentMethod ?? null);
	
	const [selectedPaymentMethodCountryCode, setSelectedPaymentMethodCountryCode] =
		useState<GetFeeParameters['methodCountryCode']>();
	
	const [fee, setFee] = useState<GetFeeResponse | null>(storybookFee ?? null);
	const [isFeeLoading, setIsFeeLoading] = useState(false);
	const [paymentMethods, setPaymentMethods] = useState<GetPaymentMethodsResponse | null>(
		storybookPaymentMethods ?? null
	);
	
	const [selectedSavedBankID, setSelectedSavedBankID] = useState<string>('');
	const [selectedSavedCardID, setSelectedSavedCardID] = useState<string>('');
	const [errorMessage, setErrorMessage] = useState('');
	const [paymentSucceeded, setPaymentSucceeded] = useState(false);
	
	
	// Use ref
	const paymentFormRef = useRef<StripePaymentElementRef>(null);
	
	
	// Use React Router functionality
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	
	
	// Get payment option ID
	const paymentOptionID = searchParams.get('id');
	const redirectStatus = searchParams.get('redirect_status');
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const globalSignIn = useAppSelector((state) => state.self.globalSignIn);
	
	
	// Load payment option on mount
	useEffect(() => {
		// Return early
		if (!paymentOptionID) {
			return;
		}
		
		
		// Call API
		void dispatch(
			apiActions.call<undefined, undefined, GetPaymentOptionResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/payment-option/${paymentOptionID}`,
				completion: (json) => {
					setPaymentOption(json.response);
					setSelectedAmount(json.response.amounts[0]);
				},
			})
		);
	}, [dispatch, paymentOptionID]);
	
	
	// Load payment methods on mount, if not a global sign in user
	useEffect(() => {
		// Return early
		if (!paymentOptionID || globalSignIn) {
			return;
		}
		
		
		// Call API
		void dispatch(
			apiActions.call<undefined, undefined, GetPaymentMethodsResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/member/payment-methods`,
				completion: (json) => {
					setPaymentMethods(json.response);
				},
			})
		);
	}, [dispatch, paymentOptionID, globalSignIn]);
	
	
	// Initialize
	//  -> Payment method types are treated as available until payment methods load
	const paymentMethodsLoaded = paymentMethods !== null;
	const paymentMethodTypeStatuses = paymentMethods ? determinePaymentMethodTypeStatuses(paymentMethods) : null;
	
	const availablePaymentMethodTypes =
		paymentOption?.paymentMethods.filter(
			(type) => !paymentMethodTypeStatuses || paymentMethodTypeStatuses[type] === 'AVAILABLE'
		) ?? [];
	
	const unavailablePaymentMethodTypes =
		paymentOption?.paymentMethods.filter(
			(type) => paymentMethodTypeStatuses && paymentMethodTypeStatuses[type] !== 'AVAILABLE'
		) ?? [];
	
	const onlyAvailablePaymentMethodType =
		availablePaymentMethodTypes.length === 1 ? availablePaymentMethodTypes[0] : null;
	
	
	// Select the payment method type when it's the only one available
	//  -> Not set by an effect, which would make its section animate open on load
	const selectedPaymentMethodType = onlyAvailablePaymentMethodType ?? chosenPaymentMethodType;
	
	
	// Load fees when selected amount or valid payment method type changes
	useEffect(() => {
		// Return early
		if (
			!selectedPaymentMethodType ||
			!paymentOptionID ||
			!selectedAmount ||
			selectedAmount < (paymentOption?.minimumAmount ?? 0) ||
			!paymentOption?.passTransactionFees
		) {
			return;
		}
		
		setIsFeeLoading(!storybookFee);
		
		
		// Call API
		void dispatch(
			apiActions.call<GetFeeParameters, undefined, GetFeeResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/payment-option/${paymentOptionID}/fee`,
				completion: (json) => {
					setFee(json.response);
					setIsFeeLoading(false);
				},
				parameters: {
					desiredAmount: selectedAmount.toString(),
					methodType: selectedPaymentMethodType,
					...(selectedPaymentMethodType === 'CARD' ? { methodCountryCode: selectedPaymentMethodCountryCode } : {}),
				},
			})
		);
	}, [
		dispatch,
		selectedPaymentMethodType,
		selectedAmount,
		paymentOptionID,
		selectedPaymentMethodCountryCode,
		paymentOption?.minimumAmount,
		paymentOption?.passTransactionFees,
		storybookFee,
	]);
	
	
	// Initialize
	const hasCardMethod = availablePaymentMethodTypes.includes('CARD');
	const hasBankMethod = availablePaymentMethodTypes.includes('BANK_ACCOUNT');
	const hasBothMethods = hasCardMethod && hasBankMethod;
	const savedBankAccounts = paymentMethods?.paymentMethods.filter(
		(paymentMethod) => paymentMethod.type === 'BANK_ACCOUNT' && paymentMethod.status === 'ACTIVE'
	);
	
	const savedCards = paymentMethods?.paymentMethods.filter(
		(paymentMethod): paymentMethod is Card => paymentMethod.type === 'CARD'
	);
	
	const effectiveBankID = selectedSavedBankID
		? selectedSavedBankID
		: savedBankAccounts?.[0]?.id
			? savedBankAccounts?.[0]?.id
			: '';
	
	
	// Handle payment success
	const handlePaymentSuccess = useCallback(() => setPaymentSucceeded(true), []);
	
	
	// Use hooks
	const { handleSubmit: handleBankSubmit } = useBankAccountPayment({
		stripeAccountID: paymentOption?.stripeAccountID ?? '',
		selectedBankID: effectiveBankID,
		onSuccess: handlePaymentSuccess,
	});
	
	const { handleSubmit: handleSavedCardSubmit } = useSavedCardPayment({
		stripeAccountID: paymentOption?.stripeAccountID ?? '',
		selectedCardID: selectedSavedCardID,
		onSuccess: handlePaymentSuccess,
	});
	
	
	// Handle missing payment option ID
	useEffect(() => {
		if (!paymentOptionID) {
			navigate('../', { replace: true });
		}
	}, [paymentOptionID, navigate]);
	
	
	// Handle payment method click
	const handleMethodClick = (method: 'CARD' | 'BANK_ACCOUNT') => {
		// Clicking the same method shouldn't change anything
		if (selectedPaymentMethodType === method) {
			return;
		}
		
		setErrorMessage('');
		setChosenPaymentMethodType(method);
	};
	
	
	// Handle card selection change
	const handleCardSelectionChange = (newValue: string | number) => {
		const cardID = newValue.toString();
		setSelectedSavedCardID(cardID);
		
		const savedCard = savedCards?.find((card) => card.id === cardID);
		setSelectedPaymentMethodCountryCode(savedCard?.country ?? undefined);
	};
	
	
	// Guard against submitting without a valid payment method selection
	const handleSubmitCapture: React.FormEventHandler<HTMLDivElement> = (event) => {
		if (!selectedPaymentMethodType || (selectedPaymentMethodType === 'BANK_ACCOUNT' && !effectiveBankID)) {
			event.preventDefault();
			event.stopPropagation();
			setErrorMessage('This is required.');
		}
	};
	
	
	// Build form options
	const formOptions: APIFormOptions<GetPaymentIntentParameters, undefined, GetPaymentIntentResponse> = {
		action: 'GET',
		uri: `/website/${window.location.hostname}/payment-option/${paymentOptionID ?? ''}/payment-intent`,
		submitButton: {
			text: 'Pay now',
			disabled: !paymentMethodsLoaded,
		},
		appearsOnBackground: false,
		recoverState: false,
		extraParameters: {
			methodType: selectedPaymentMethodType === 'BANK_ACCOUNT' ? 'BANK_ACCOUNT' : 'CARD',
			...(selectedPaymentMethodType === 'CARD' && selectedSavedCardID !== ''
				? { methodCountryCode: selectedPaymentMethodCountryCode }
				: {}),
			...(paymentOption?.amountType !== 'SINGLE_FIXED' ? { amount: currency(selectedAmount ?? 0).toString() } : {}),
		},
	};
	
	
	// Handle form completion by confirming the payment intent with the appropriate payment method
	const handleFormCompletion = async (json: SuccessfulAPIResponse<GetPaymentIntentResponse>) => {
		if (selectedPaymentMethodType === 'BANK_ACCOUNT') {
			await handleBankSubmit(json.response);
			
			return;
		}
		
		if (selectedSavedCardID) {
			await handleSavedCardSubmit(json.response);
			
			return;
		}
		
		await paymentFormRef.current?.handleSubmit(json.response);
	};
	
	
	// If payment was submitted successfully, show the confirmation immediately
	if (paymentSucceeded || redirectStatus === 'succeeded') {
		if (!paymentOptionID) {
			return null;
		}
		
		return (
			<React.Fragment>
				<Helmet title='Make online payment' />
				<Heading>
					Make online payment - “{paymentOption?.title ? paymentOption.title : <Skeleton inline width='8rem' />}”
				</Heading>
				<FormSubmitted
					header='Payment submitted!'
					body={
						<React.Fragment>
							You’ve successfully submitted a payment for “
							{paymentOption ? paymentOption.title : <Skeleton inline width='8rem' />}
							,” and it will be processed shortly. View your <Link to='../history'>payment history &rarr;</Link>
						</React.Fragment>
					}
				/>
			</React.Fragment>
		);
	}
	
	
	// If there's no payment option ID, then display nothing
	if (!paymentOptionID) {
		return null;
	}
	
	
	// If loading, then display skeletons
	if (paymentOption === null) {
		return (
			<React.Fragment>
				<Helmet title='Make online payment' />
				<Heading>
					Make online payment - “<Skeleton inline width='8rem' />”
				</Heading>
				
				<Panel>
					<Header>Amount</Header>
					
					<p>
						This online payment <Skeleton inline width='3rem' />, and <Skeleton inline width='16rem' />.
					</p>
				</Panel>
				
				<Panel>
					<Header>Payment method</Header>
					
					<Skeleton width='8.5rem' />
					<Skeleton width='7rem' />
				</Panel>
				
				<PayNow moreSpace={false}>
					<form>
						<ConfirmAndPayPanel>
							<Header>Confirm and pay</Header>
						</ConfirmAndPayPanel>
						
						<div>
							<Button type='submit' useBackgroundColorWithFocus={false} disabled>
								Pay now
							</Button>
						</div>
					</form>
					
					<ConfirmAndPayFooter />
				</PayNow>
			</React.Fragment>
		);
	}
	
	
	// If none of the payment option's methods are available, members can't make payments
	if (availablePaymentMethodTypes.length === 0) {
		return (
			<React.Fragment>
				<PageTitle documentTitle='Make online payment'>{`Make online payment - “${paymentOption.title}”`}</PageTitle>
				
				{unavailablePaymentMethodTypes.every((type) => paymentMethodTypeStatuses?.[type] === 'PENDING_VERIFICATION') ? (
					<Notice color='yellow'>
						<strong>Heads up.</strong> Payments cannot be made until our payment processing partner finishes reviewing
						your community’s information.
					</Notice>
				) : (
					<Notice color='danger'>
						<strong>Oh snap!</strong> Payments cannot be made until a website admin provides requested information.
					</Notice>
				)}
			</React.Fragment>
		);
	}
	
	
	// Parse and sanitize notes
	const sanitizedNotes = {
		__html: parseAndSanitizeMarkdown(paymentOption.notes ?? ''),
	};
	
	
	// Initialize
	const formattedAmountWithCurrencyCode = formatCurrency(selectedAmount ?? 0, {
		currency: paymentOption.currencyCode,
		hideCentsIfInteger: true,
	});
	
	const formattedAmountWithoutCurrencyCode = formatCurrency(selectedAmount ?? 0, {
		currency: paymentOption.currencyCode,
		hideCurrencyCode: true,
	});
	
	const formattedMinimumAmount = formatCurrency(paymentOption.minimumAmount, {
		currency: paymentOption.currencyCode,
		hideCurrencyCode: true,
	});
	
	const formattedFee = formatCurrency(fee?.fee ?? 0, {
		currency: paymentOption.currencyCode,
		hideCurrencyCode: true,
	});
	
	const formattedTotal = formatCurrency((selectedAmount ?? 0) + (fee?.fee ?? 0), {
		currency: paymentOption.currencyCode,
		hideCurrencyCode: true,
	});
	
	
	// Map amount type to message
	const amountTypeMessages: Record<GetPaymentOptionResponse['amountType'], React.ReactNode> = {
		SINGLE_FIXED: (
			<React.Fragment>
				is for <strong>{formattedAmountWithCurrencyCode}</strong>
			</React.Fragment>
		),
		MULTIPLE_FIXED: 'has several preset choices for the amount',
		FREEFORM: 'allows you to specify the amount',
	};
	
	
	// Initialize
	const showChargedAmount =
		Boolean(selectedAmount) &&
		(selectedAmount ?? 0) >= paymentOption.minimumAmount &&
		Boolean(selectedPaymentMethodType);
	
	const showMinimumAmountWarning = (selectedAmount ?? 0) > 0 && (selectedAmount ?? 0) < paymentOption.minimumAmount;
	
	
	// Return JSX
	return (
		<React.Fragment>
			<PageTitle documentTitle='Make online payment'>{`Make online payment - “${paymentOption.title}”`}</PageTitle>
			
			{paymentOption.notes && <Notes dangerouslySetInnerHTML={sanitizedNotes} />}
			
			<PayNow onSubmitCapture={handleSubmitCapture} moreSpace={showChargedAmount || showMinimumAmountWarning}>
				<APIForm options={formOptions} onCompletion={handleFormCompletion}>
					<Panel>
						<Header>Amount</Header>
						
						<p>
							This online payment {amountTypeMessages[paymentOption.amountType]}, and{' '}
							{paymentOption.passTransactionFees
								? 'a fee will be applied based on the payment method used below'
								: 'there are no fees'}
							.
						</p>
						
						{paymentOption.amountType === 'MULTIPLE_FIXED' && (
							<Amount>
								<Select
									name='amount'
									label='Choose amount'
									required
									onChange={(newValue) => setSelectedAmount(Number(newValue))}
									initialValue={selectedAmount ?? undefined}
								>
									{paymentOption.amounts.map((amount, i) => (
										<option value={amount} key={i}>
											{formatCurrency(amount, {
												currency: paymentOption.currencyCode,
											})}
										</option>
									))}
								</Select>
							</Amount>
						)}
						
						{paymentOption.amountType === 'FREEFORM' && (
							<Amount>
								<Input
									name='amount'
									label='Specify amount'
									type='number'
									initialValue={selectedAmount?.toString() ?? undefined}
									min={paymentOption.minimumAmount}
									step={0.01}
									prefix={paymentOption.currencySymbol}
									suffix={paymentOption.currencyCode}
									onChange={(newValue) => setSelectedAmount(Number(newValue))}
									placeholder='0.00'
									required
								/>
							</Amount>
						)}
					</Panel>
					
					<Panel>
						<Header>Payment method</Header>
						
						{unavailablePaymentMethodTypes.map((type) => (
							<UnavailablePaymentMethodTypeNotice
								key={type}
								page='MAKE_PAYMENT'
								type={type}
								pendingVerification={paymentMethodTypeStatuses?.[type] === 'PENDING_VERIFICATION'}
							/>
						))}
						
						{hasBothMethods && (
							<PaymentMethodType {...helperFunctions.buttonize(() => handleMethodClick('CARD'))}>
								<ChevronIcon icon={faChevronRight} $isExpanded={selectedPaymentMethodType === 'CARD'} />
								Credit/debit card
							</PaymentMethodType>
						)}
						
						{hasCardMethod && (
							<ContentSection open={selectedPaymentMethodType === 'CARD'}>
								<ContentSectionInner>
									<MethodContent bottomSpacing={hasBothMethods} marginTop={hasBothMethods}>
										{(savedCards?.length ?? 0) > 0 && (
											<CardSelectorWrapper isNewCardSelected={selectedSavedCardID === ''}>
												<Select
													name='savedCard'
													label='Use card'
													exclude
													onChange={handleCardSelectionChange}
													initialValue={selectedSavedCardID}
												>
													<option value=''>New card</option>
													{savedCards?.map((card) => (
														<option value={card.id} key={card.id}>
															{card.brand} ending in {card.last4}
														</option>
													))}
												</Select>
											</CardSelectorWrapper>
										)}
										
										{/* LATER: Render Stripe's payment element instead of displaying this placeholder in screenshots */}
										<AnimateHeight height={selectedSavedCardID === '' ? 'auto' : 0} duration={400} animateOpacity>
											{!paymentMethodsLoaded ? (
												<Skeleton width='16rem' />
											) : isScreenshot() ? (
												<Placeholder>Stripe payment element placeholder</Placeholder>
											) : (
												<StripeElements
													ref={paymentFormRef}
													paymentOption={{ ...paymentOption, id: paymentOptionID }}
													selectedAmount={selectedAmount ?? 0}
													onSuccess={handlePaymentSuccess}
													onPaymentMethodCountryCodeChange={setSelectedPaymentMethodCountryCode}
													isActive={selectedPaymentMethodType === 'CARD' && selectedSavedCardID === ''}
												/>
											)}
										</AnimateHeight>
									</MethodContent>
								</ContentSectionInner>
							</ContentSection>
						)}
						
						{hasBothMethods && (
							<PaymentMethodType {...helperFunctions.buttonize(() => handleMethodClick('BANK_ACCOUNT'))}>
								<ChevronIcon icon={faChevronRight} $isExpanded={selectedPaymentMethodType === 'BANK_ACCOUNT'} />
								Bank account
							</PaymentMethodType>
						)}
						
						{hasBankMethod && (
							<AnimateHeight
								height={selectedPaymentMethodType === 'BANK_ACCOUNT' ? 'auto' : 0}
								duration={400}
								animateOpacity
							>
								<MethodContent marginTop={hasBothMethods}>
									{!paymentMethodsLoaded ? (
										<Skeleton width='16rem' />
									) : (savedBankAccounts?.length ?? 0) === 0 ? (
										<span>
											To pay with a bank account, you must first <Link to='../methods'>add one here</Link>.
										</span>
									) : (
										<Select
											name='savedBankAccount'
											label='Select bank account…'
											required={selectedPaymentMethodType === 'BANK_ACCOUNT'}
											exclude
											onChange={(newValue) => setSelectedSavedBankID(newValue.toString())}
											initialValue={effectiveBankID ? effectiveBankID : undefined}
										>
											{savedBankAccounts?.map((account) => (
												<option value={account.id} key={account.id}>
													{account.brand} ending in {account.last4}
												</option>
											))}
										</Select>
									)}
								</MethodContent>
							</AnimateHeight>
						)}
						
						{errorMessage && <FieldErrorMessage message={errorMessage} />}
					</Panel>
					
					<ConfirmAndPayPanel>
						<Header>Confirm and pay</Header>
						
						{showChargedAmount && (
							<span>
								You will be charged{' '}
								{isFeeLoading ? (
									<React.Fragment>
										<Skeleton inline width='2.5rem' />
										{paymentOption.passTransactionFees && (
											<span>
												{' '}
												({formattedAmountWithoutCurrencyCode} payment + <Skeleton inline width='2.5rem' /> fee)
											</span>
										)}
										.
									</React.Fragment>
								) : (
									<React.Fragment>
										<strong>{formattedTotal}</strong>
										{(fee?.fee ?? 0) > 0 ? (
											<span>
												{' '}
												({formattedAmountWithoutCurrencyCode} payment + {formattedFee} fee).
												{fee?.includesInternationalFee && selectedPaymentMethodType === 'CARD' && (
													<span> The fee could be lowered by using a non-international card.</span>
												)}
											</span>
										) : (
											<span> for this payment.</span>
										)}
									</React.Fragment>
								)}
							</span>
						)}
						
						{showMinimumAmountWarning && (
							<MinimumAmountWarning>
								The amount you specify above must be at least {formattedMinimumAmount}.
							</MinimumAmountWarning>
						)}
					</ConfirmAndPayPanel>
				</APIForm>
				
				<ConfirmAndPayFooter>
					<AnimateHeight
						height={selectedPaymentMethodType === 'BANK_ACCOUNT' && effectiveBankID ? 'auto' : 0}
						duration={400}
						animateOpacity
					>
						<Mandate>
							<small className='is-gray'>
								By submitting this payment, you are authorizing {paymentMethods?.communityLegalName} to debit your bank
								account for the amount mentioned above, pursuant to these{' '}
								{/* eslint-disable-next-line react/jsx-no-target-blank */}
								<a
									target='_blank'
									href={`https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/policies/terms-of-service`}
								>
									terms of service
								</a>
								.
							</small>
						</Mandate>
					</AnimateHeight>
				</ConfirmAndPayFooter>
			</PayNow>
		</React.Fragment>
	);
};

export default MakePayment;
